// src/features/vertraege/services/VertragService.ts
//
// Bindeglied zwischen der Vertragsoberflaeche und der Datenhaltung.
//
// Lesen laeuft direkt ueber supabase-js, Erzeugen und Rendern ueber die
// Netlify-Function vertrag-erzeugen. Das PDF entsteht bewusst nur
// serverseitig, damit Entwurf und spaetere Endfassung aus demselben
// Renderer stammen und der Hash reproduzierbar bleibt.

import { supabase } from '../../../shared/services/supabase';
import { netlifyFetch } from '../../../shared/services/netlifyFetch';
import {
  Vertrag,
  Vertragsvorlage,
  Vertretungsberechtigter,
  VertragErzeugenAntwort,
  VertragErzeugenParams
} from '../types';

class VertragService {
  /**
   * E-Mail des angemeldeten Mitarbeiters, sofern ermittelbar. Wird nur zur
   * Zuordnung des Erstellers verwendet und ist nicht zwingend.
   */
  private async aktuelleMitarbeiterEmail(): Promise<string | undefined> {
    try {
      const { data } = await supabase.auth.getUser();
      return data?.user?.email || undefined;
    } catch {
      return undefined;
    }
  }

  /** Alle Verträge eines Kunden, neueste zuerst. */
  async getVertraege(customerId: string): Promise<Vertrag[]> {
    const { data, error } = await supabase
      .from('vertraege')
      .select('*')
      .eq('customer_id', customerId)
      .order('created_at', { ascending: false })
      .limit(100000);

    if (error) {
      console.error('[VertragService] getVertraege error:', error);
      return [];
    }
    return (data || []) as Vertrag[];
  }

  /** Die aktive Vorlage eines Vertragstyps. */
  async getAktiveVorlage(typ = 'payroll_as_a_service'): Promise<Vertragsvorlage | null> {
    const { data, error } = await supabase
      .from('vertragsvorlagen')
      .select('*')
      .eq('typ', typ)
      .eq('aktiv', true)
      .maybeSingle();

    if (error) {
      console.error('[VertragService] getAktiveVorlage error:', error);
      return null;
    }
    return (data as Vertragsvorlage) || null;
  }

  /** Aktive, vertretungsberechtigte Ansprechpartner eines Kunden. */
  async getVertretungsberechtigte(customerId: string): Promise<Vertretungsberechtigter[]> {
    const { data, error } = await supabase
      .from('customer_contacts')
      .select('id, vorname, nachname, email, geschlecht, funktion')
      .eq('customer_id', customerId)
      .eq('is_active', true)
      .eq('ist_vertretungsberechtigt', true)
      .order('sort_order', { ascending: true })
      .limit(100000);

    if (error) {
      console.error('[VertragService] getVertretungsberechtigte error:', error);
      return [];
    }
    return (data || []) as Vertretungsberechtigter[];
  }

  /**
   * Ruft die Erzeugung auf. Mit previewOnly wird ausschliesslich gerendert,
   * ohne jeden Schreibvorgang. Ohne previewOnly entsteht bzw. aktualisiert
   * sich der Entwurf.
   */
  async erzeugen(params: VertragErzeugenParams): Promise<VertragErzeugenAntwort> {
    try {
      const email = await this.aktuelleMitarbeiterEmail();

      const response = await netlifyFetch('/.netlify/functions/vertrag-erzeugen', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_id: params.customerId,
          vertrag_id: params.vertragId,
          empfaenger_contact_id: params.empfaengerContactId,
          variablen: params.variablen,
          vorlage_typ: params.vorlageTyp,
          preview_only: params.previewOnly === true,
          erstellt_von_email: email
        })
      });

      const ergebnis = await response.json();

      if (!response.ok || !ergebnis.success) {
        return {
          success: false,
          error: ergebnis.error || 'Der Vertrag konnte nicht erzeugt werden.',
          fehlendeDaten: ergebnis.fehlende_daten,
          auswahl: ergebnis.auswahl
        };
      }

      return {
        success: true,
        preview: ergebnis.preview === true,
        vertrag: ergebnis.vertrag,
        pdfBase64: ergebnis.pdf,
        filename: ergebnis.filename,
        variablen: ergebnis.variablen,
        hash: ergebnis.hash
      };
    } catch (error) {
      console.error('[VertragService] erzeugen error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unbekannter Fehler beim Erzeugen'
      };
    }
  }

  /**
   * Löscht einen Entwurf. Nur Entwürfe sind löschbar, alles ab dem Versand
   * bleibt als Historie erhalten.
   */
  async entwurfLoeschen(vertragId: string): Promise<{ success: boolean; error?: string }> {
    const { data, error } = await supabase
      .from('vertraege')
      .delete()
      .eq('id', vertragId)
      .eq('status', 'entwurf')
      .select('id');

    if (error) {
      console.error('[VertragService] entwurfLoeschen error:', error);
      return { success: false, error: error.message };
    }
    if (!data || data.length === 0) {
      return { success: false, error: 'Nur Entwürfe können gelöscht werden.' };
    }
    return { success: true };
  }

  /** Wandelt ein Base64-PDF in eine Blob-URL für Vorschau und Download. */
  base64ZuBlobUrl(base64: string): string {
    const binaer = atob(base64);
    const bytes = new Uint8Array(binaer.length);
    for (let i = 0; i < binaer.length; i++) {
      bytes[i] = binaer.charCodeAt(i);
    }
    return URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
  }
}

export const vertragService = new VertragService();
